import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { OctagonAlert, UserPlus } from 'lucide-react';
import {
  isOpenAssignment,
  type Assignment,
  type PayableStage,
  type WorkflowStage,
} from '@tailoring-crm/shared';
import {
  useCreateAssignment,
  useOverrideRate,
  useReassign,
  useResolveProblem,
} from '../../lib/assignments/use-assignments.js';
import { formatCurrency, formatDate, formatMinutes } from '../../lib/format.js';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { AssignDialog, type AssignDialogResult } from './assign-dialog.js';

/**
 * Who has this garment, on the order page.
 *
 * The whole history is shown, not only the open job: a garment that has been through three
 * people is a garment somebody will ask about, and the closed rows carry the reason it moved
 * and the rate each person was on. That is the same reason a reassignment writes a new row
 * rather than editing the old one — this list is what that decision was for.
 *
 * Money IS shown here. This is the owner's page, behind `assignments.read` at shop scope; the
 * karigar's screen is a different route with a different payload.
 */
export interface ItemAssignmentsProps {
  itemId: string;
  /**
   * Every assignment on this ORDER, fetched once by the page.
   *
   * Passed in rather than queried per garment: a bill with six blouses would otherwise open the
   * order page with six identical round trips, and the answer to all of them is one list.
   */
  rows: readonly Assignment[];
  garmentLabel: string;
  itemStage: WorkflowStage;
  stageLabel: (stage: PayableStage) => string;
  /** False for a caller who may not hand work out. */
  canAssign: boolean;
  /** False for a caller who may not agree a different rate on one job. */
  canOverrideRate: boolean;
}

export function ItemAssignments({
  itemId,
  rows: orderRows,
  garmentLabel,
  itemStage,
  stageLabel,
  canAssign,
  canOverrideRate,
}: ItemAssignmentsProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();

  const create = useCreateAssignment();
  const reassign = useReassign();
  const override = useOverrideRate();
  const resolve = useResolveProblem();

  const [assigning, setAssigning] = useState(false);
  const [movingTo, setMovingTo] = useState<Assignment | null>(null);
  const [repricing, setRepricing] = useState<Assignment | null>(null);
  const [reason, setReason] = useState('');
  const [rupees, setRupees] = useState('');

  const rows = orderRows.filter((row) => row.itemId === itemId);
  const open = rows.filter((row) => isOpenAssignment(row.status));

  const report = (error: unknown): void => {
    toast({
      title: error instanceof Error ? error.message : t('error.unexpected'),
      variant: 'destructive',
    });
  };

  // The reason is the reassignment's, not the assignment's — it is dropped here rather than sent
  // to an endpoint that would ignore it.
  const onAssign = async ({ reason: _reason, ...input }: AssignDialogResult): Promise<void> => {
    try {
      const result = await create.mutateAsync({ itemId, ...input });
      toast({
        title: t('assignments.assigned', { name: result.assignment.assignedToName }),
        ...(result.warnings.length > 0
          ? { description: result.warnings.map((one) => one.message).join(' · ') }
          : {}),
        variant: result.warnings.length > 0 ? 'default' : 'success',
      });
      setAssigning(false);
    } catch (error) {
      report(error);
      setAssigning(false);
    }
  };

  const onReassign = async (input: { assignedToUserId: string; reason: string }): Promise<void> => {
    if (!movingTo) return;
    try {
      const result = await reassign.mutateAsync({
        assignmentId: movingTo.id,
        input: { assignedToUserId: input.assignedToUserId, reason: input.reason },
      });
      toast({
        title: t('assignments.assigned', { name: result.assignment.assignedToName }),
        variant: 'success',
      });
    } catch (error) {
      report(error);
    } finally {
      setMovingTo(null);
    }
  };

  const onOverride = async (): Promise<void> => {
    if (!repricing) return;
    try {
      // Rupees in the box, paise on the wire. Never a float anywhere in between.
      const ratePaise = Math.round(Number(rupees) * 100);
      await override.mutateAsync({
        assignmentId: repricing.id,
        input: { ratePaise, reason: reason.trim() },
      });
      toast({ title: t('assignments.rateChanged'), variant: 'success' });
    } catch (error) {
      report(error);
    } finally {
      setRepricing(null);
      setReason('');
      setRupees('');
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        {t('assignments.itemHeading')}
      </p>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('assignments.itemNone')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-start justify-between gap-2 border-b border-border pb-2 last:border-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                  {row.assignedToName}
                  <Badge variant="outline">{stageLabel(row.stage)}</Badge>
                  <Badge variant={isOpenAssignment(row.status) ? 'secondary' : 'default'}>
                    {t(`assignments.status.${row.status}`)}
                  </Badge>
                  {row.problem && row.problem.resolvedAt === null ? (
                    <Badge variant="destructive">
                      <OctagonAlert aria-hidden="true" className="mr-1 size-3" />
                      {t(`assignments.problemKind.${row.problem.kind}`)}
                    </Badge>
                  ) : null}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('assignments.assignedOn', { date: formatDate(row.assignedAt) })}
                  {row.completedAt
                    ? ` · ${t('assignments.completedOn', { date: formatDate(row.completedAt) })}`
                    : ''}
                  {row.reassignReason ? ` · ${row.reassignReason}` : ''}
                </p>
                {/* How long this was expected to take. A job nobody estimated says so, rather
                    than showing 00:00 — "not estimated" is not "takes no time". */}
                <p className="text-sm text-muted-foreground tabular-nums">
                  {row.estimatedMinutes === null
                    ? t('assignments.estimateNone')
                    : t('assignments.estimate', {
                        duration: formatMinutes(row.estimatedMinutes),
                      })}
                </p>
                {row.problem && row.problem.resolvedAt === null && row.problem.note ? (
                  <p className="text-sm text-destructive">{row.problem.note}</p>
                ) : null}
              </div>

              <div className="flex flex-col items-end gap-1">
                <p className="text-sm font-semibold tabular-nums">
                  {row.earningPaise === null
                    ? t('assignments.noRate')
                    : formatCurrency(row.earningPaise)}
                  {row.rateOverridden ? (
                    <Badge variant="warning" className="ml-2">
                      {t('assignments.rateOverridden')}
                    </Badge>
                  ) : null}
                </p>
                <div className="flex flex-wrap justify-end gap-1">
                  {canOverrideRate && row.settledAt === null && isOpenAssignment(row.status) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRepricing(row);
                        setRupees(row.ratePaise === null ? '' : String(row.ratePaise / 100));
                      }}
                    >
                      {t('assignments.changeRate')}
                    </Button>
                  ) : null}
                  {canAssign && isOpenAssignment(row.status) ? (
                    <Button variant="ghost" size="sm" onClick={() => setMovingTo(row)}>
                      {t('assignments.reassignShort')}
                    </Button>
                  ) : null}
                  {canAssign && row.problem && row.problem.resolvedAt === null ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void resolve.mutateAsync({ assignmentId: row.id })}
                    >
                      {t('assignments.resolveProblem')}
                    </Button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canAssign ? (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => setAssigning(true)}
        >
          <UserPlus aria-hidden="true" className="mr-1.5 size-3.5" />
          {open.length > 0 ? t('assignments.assignAnother') : t('assignments.assignAction')}
        </Button>
      ) : null}

      {assigning ? (
        <AssignDialog
          subject={garmentLabel}
          currentStage={itemStage}
          stageLabel={stageLabel}
          pending={create.isPending}
          onCancel={() => setAssigning(false)}
          onConfirm={(input) => void onAssign(input)}
        />
      ) : null}

      {/* Reassignment reuses the same picker, pinned to this job's stage and asking why. */}
      {movingTo ? (
        <AssignDialog
          subject={garmentLabel}
          currentStage={movingTo.stage}
          stages={[movingTo.stage]}
          stageLabel={stageLabel}
          requireReason
          pending={reassign.isPending}
          onCancel={() => setMovingTo(null)}
          onConfirm={(input) => void onReassign(input)}
        />
      ) : null}

      {repricing ? (
        <Dialog open onOpenChange={(next) => !next && setRepricing(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t('assignments.changeRateTitle', { name: repricing.assignedToName })}
              </DialogTitle>
              <DialogDescription>{t('assignments.changeRateHint')}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="override-rate">{t('assignments.rateLabel')}</Label>
                <Input
                  id="override-rate"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  value={rupees}
                  onChange={(event) => setRupees(event.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="override-reason">{t('assignments.reasonLabel')}</Label>
                <Input
                  id="override-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={t('assignments.rateReasonPlaceholder')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setRepricing(null);
                  setReason('');
                  setRupees('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                disabled={reason.trim().length < 3 || rupees === '' || override.isPending}
                onClick={() => void onOverride()}
              >
                {t('assignments.changeRateConfirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
